<script>
import Vue from 'vue';
import AssetCalendarCell from '../../../calendar/partials/TeamCalendarCell';
import CalendarMixins from '../../../../shared/mixins/calendar-mixins';

const AssetCalendarCellClass = Vue.extend(AssetCalendarCell);

export default {
    mixins: [CalendarMixins],
    props: {
        asset: {
            type: Object,
            required: true,
        },
        leadTime: {
            required: true,
        },
        busy: {
            default: false,
        },
    },

    data() {
        return {
            form: new SparkForm({
                end_date: null,
                start_date: null,
                enabled: 1,
                base_price: null,
                units_available: null,
                units_posted: null,
                note: null,
            }),
            groupForm: new SparkForm({
                home_games: false,
                road_games: false,
                season_long: false,
                year_long: false,
                enabled: 1,
                base_price: null,
                bundle_only: null,
                units_available: null,
                units_posted: null,
            }),
            leadTimeDate: null,
        };
    },

    computed: {
        /**
         * returns the dynamic pricng adjusted price for form display
         */
        adjustedPrice() {
            let unitPrice = _.toNumber(this.form.base_price);
            return this.calendar.selectedPriceModifier && unitPrice
                ? unitPrice + this.calendar.selectedPriceModifier.multiplier * unitPrice
                : unitPrice;
        },
        /**
         * Url for data loading into calendar - required for mixin.
         */
        dataUrl() {
            return '/api/team/assets/' + this.asset.id + '/events';
        },

        currentSchedule() {
            return _.has(Spark, 'currentSchedule.id');
        },

        calendarSelectionEditable() {
            return !this.beforeLeadTime(_.get(this.calendar, 'selectedStartDate.start'));
        },
    },

    created() {
        Vue.set(this.groupForm, 'home_games', this.asset.home_games);
        Vue.set(this.groupForm, 'road_games', this.asset.road_games);
        Vue.set(this.groupForm, 'season_long', this.asset.season_long);
        Vue.set(this.groupForm, 'year_long', this.asset.year_long);
        Bus.$on('calendarDatepickerChange', this.addCustomPricingGroup);
    },

    methods: {
        beforeLeadTime(date) {
            return !dateTimeObject(date).isAfter(this.leadTimeDate);
        },
        dayRender(date, cell) {
            if (this.beforeLeadTime(date)) {
                cell.addClass('fc-disabled-day');
            }
        },
        eventRender(event, element) {
            const instance = new AssetCalendarCellClass({
                propsData: {
                    event: event,
                    element: element,
                    leadTime: this.leadTime,
                    config: {
                        dynamicPricingIcon: true,
                        autoRestockIcon: this.asset.restock,
                    },
                },
            });
            instance.$mount();
            element.html(instance.$el);
        },

        onGetCalendarData(events) {
            const _this = this;
            return _.map(events, (event) => {
                if (_this.beforeLeadTime(event.start)) {
                    event.expired = true;
                    event.className.push('expired');
                } else {
                    event.expired = false;
                    _.remove(event.className, (name) => _.includes(['expired'], name));
                }

                return event;
            });
        },

        /**
         * Fires when a selection is made on the calendar
         */
        onCalendarSelection() {
            this.setFormValues(this.calendar.selectedEvent);
        },

        /**
         * Sets all form values with saved or default values
         */
        setFormValues(event) {
            this.form.enabled = _.get(event, 'product.enabled', 1);
            this.form.base_price = _.get(event, 'product.base_price', this.asset.online_price);
            this.form.units_available = _.get(event, 'product.units_available', this.asset.base_units_available);
            this.form.units_posted = this.asset.bundle_only ? 0 : _.get(event, 'product.units_posted', this.asset.base_units_posted);
            this.form.note = _.get(event, 'product.note');
        },

        /**
         * Cancel form
         */
        cancel() {
            this.setFormValues([]);
            this.calendar.obj.fullCalendar('unselect');
            this.clearCalendarSelection();
        },

        /**
         * Save form
         */
        save() {
            const _this = this;
            this.form.start_date = this.calendar.start_date;
            this.form.end_date = this.calendar.end_date ? this.calendar.end_date.clone().subtract(1, 'days') : this.calendar.start_date;

            Spark.post('/api/team/assets/' + this.asset.id + '/products', this.form).then(() => {
                _this.$noty.success('Asset Saved');
                this.calendar.obj.fullCalendar('refetchEvents');
            });
        },

        /**
         * Clear all pricing
         */
        clearPricing() {
            const _this = this;
            swal({
                title: 'Are you sure?',
                type: 'warning',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: "Yes, I'm sure",
                cancelButtonText: 'Cancel',
                html: 'This will delete all the pricing for this asset!',
            }).then((response) => {
                if (response.dismiss !== 'cancel') {
                    _this.calendar.isLoading = true;

                    axios.delete('/api/team/assets/' + this.asset.id + '/products/clear').then(() => {
                        _this.calendar.obj.fullCalendar('unselect');
                        _this.calendar.obj.fullCalendar('refetchEvents');
                        _this.setFormValues([]);
                        _this.clearCalendarSelection();
                        Vue.set(this.groupForm, 'home_games', 0);
                        Vue.set(this.groupForm, 'road_games', 0);
                        Vue.set(this.groupForm, 'season_long', 0);
                        Vue.set(this.groupForm, 'year_long', 0);
                    });
                }
            });
        },

        addCustomPricingGroup() {
            const _this = this;

            swal({
                title: 'Apply pricing to selected date range?',
                type: 'warning',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Apply',
                cancelButtonText: 'Cancel',
            }).then((response) => {
                if (response.dismiss !== 'cancel') {
                    _this.calendar.isLoading = true;
                    _this.form.start_date = _this.calendar.start_date;
                    _this.form.end_date = _this.calendar.end_date ? _this.calendar.end_date.clone().subtract(1, 'days') : _this.calendar.start_date;
                    _this.form.base_price = _this.asset.online_price;
                    _this.form.units_available = _this.asset.base_units_available;
                    _this.form.units_posted = _this.asset.base_units_posted;

                    Spark.post('/api/team/assets/' + _this.asset.id + '/products', _this.form).then(() => {
                        _this.$noty.success('Prices Saved');
                        _this.calendar.obj.fullCalendar('refetchEvents');
                    });
                }
            });
        },
        addPricingGroup(attr, message) {
            const _this = this;

            let titles = {
                year_long:
                    'Apply pricing to all dates within your defined “year” as input in your Team Settings and override any previously input pricing?',
                season_long: 'Apply pricing to all dates within the season and override any previously input pricing?',
                home_games: 'Apply pricing to home games and override any previous input pricing?',
                road_games: 'Apply pricing to road games and override any previous input pricing',
            };

            swal({
                title: _.get(titles, attr),
                type: 'warning',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Apply',
                cancelButtonText: 'Cancel',
            }).then((response) => {
                if (response.dismiss !== 'cancel') {
                    _this.calendar.isLoading = true;
                    _this.groupForm.bundle_only = _this.asset.bundle_only;
                    _this.groupForm.base_price = _this.asset.online_price;
                    _this.groupForm.units_available = _this.asset.base_units_available;
                    _this.groupForm.units_posted = _this.asset.base_units_posted;

                    Spark.post('/api/team/assets/' + _this.asset.id + '/pricing-calendar/' + attr, this.groupForm)
                        .then((response) => {
                            let data = _this.$vueData.getData(response);
                            _this.groupForm.year_long = data.year_long;
                            _this.groupForm.season_long = data.season_long;
                            _this.groupForm.home_games = data.home_games;
                            _this.groupForm.road_games = data.road_games;
                            this.calendar.obj.fullCalendar('refetchEvents');
                        })
                        .finally((response) => {
                            _this.calendar.isLoading = false;
                        });
                } else {
                    Vue.set(_this.groupForm, attr, 0);
                }
            });
        },
        removePricingGroup(attr, message) {
            const _this = this;

            let titles = {
                year_long: 'Remove pricing from all dates within your defined “year”?',
                season_long: 'Remove pricing from all dates within the season?',
                home_games: 'Remove pricing from home games?',
                road_games: 'Remove pricing from road games?',
            };

            swal({
                title: _.get(titles, attr),
                type: 'warning',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'Cancel',
            }).then((response) => {
                if (response.dismiss !== 'cancel') {
                    _this.calendar.isLoading = true;

                    axios.delete('/api/team/assets/' + _this.asset.id + '/pricing-calendar/' + attr).then((response) => {
                        let data = _this.$vueData.getData(response);
                        _this.groupForm.year_long = data.year_long;
                        _this.groupForm.season_long = data.season_long;
                        _this.groupForm.home_games = data.home_games;
                        _this.groupForm.road_games = data.road_games;
                        _this.$noty.success('Prices removed');
                        this.calendar.obj.fullCalendar('refetchEvents');
                    });
                } else {
                    Vue.set(_this.groupForm, attr, 1);
                }
            });
        },
        pricingMethod(e) {
            let attr = e.target.dataset.attr;

            if (e.target.checked) {
                this.addPricingGroup(attr);
            } else {
                this.removePricingGroup(attr);
            }
        },
    },
    watch: {
        leadTime: {
            deep: true,
            handler(newVal) {
                this.leadTimeDate = dateTimeObject().businessAdd(_.get(newVal, 'days', 5));
                if (this.calendar.obj) {
                    this.calendar.obj.fullCalendar('destroy');
                    this.$nextTick(() => {
                        this.buildCalendar();
                    });
                }
            },
        },
    },
};
</script>
